import {VendorList} from "../model/vendor-list";
import {TCString} from "../tcstring/t-c-string";
import {AddtlConsent} from "../addtl-consent/addtl-consent";
import {UserSelection} from "../user-selection";
import cmpStatus from "../cmp-status";
import {EventManager} from "../api/event-manager";
import {Purpose} from "../model/purpose";
import {SpecialPurpose} from "../model/special-purpose";
import {Feature} from "../model/feature";
import {SpecialFeature} from "../model/special-feature";
import {LIPurpose} from "../model/l-i-purpose";
import {Vendor} from "../model/vendor";
import {VendorLI} from "../model/vendor-l-i";
import {AdditionalVendor} from "../model/additional-vendor";
import {log} from "../util/log";
import {CustomVendor} from "../model/custom-vendor";
import {CustomConsent} from "../custom-consent/custom-consent";
import {site} from "../context/context";
import Metadata from "../metadata";

export const CMP_API:string = '__tcfapi';
export const CMP_API_PRIVATE:string = '__tcfapiprivate';
export const CMP_VERSION:number = 2;
export const CMP_ID:number = 237;
export const CMP_SS_COOKIE:string = 'euconsent-v2';
export const CMP_ADD_CONSENT_LOCALKEY: string = 'addtl_consent';
export const CMP_ADD_CONSENT_COOKIE: string = 'euconsent-v2-addtl';
export const CMP_CUS_CONSENT_LOCALKEY: string = 'cus_consent';
export const CMP_CUS_CONSENT_COOKIE: string = 'euconsent-v2-cus';
export const CMP_META_COOKIE:string = 'cmp-cl';
export const CMP_NORMAL_EXPIRED_DAYS: number = 365;
export const CMP_CONT_EXPIRED_DAYS: number = 3;

export const CMP_SS_COOKIE_OLD:string = 'meteored_consent_v2';
export const CMP_SS_COOKIE_COPY_OLD:string = 'meteored_euconsent_v2';
export const CMP_ADD_CONSENT_COOKIE_OLD:string = `meteored_addtl_consent`;
export const CMP_META_COOKIE_OLD:string = 'meteored_cmp';

export abstract class CMP {
    /* STATIC */

    /* INSTANCE */

    private _vendorList: VendorList|undefined;
    private _tcString: TCString|undefined;
    private _addtlConsent: AddtlConsent|undefined;
    private _customConsent: CustomConsent|undefined;
    private _userSelection: UserSelection|undefined;
    private _consentAcquired: boolean = false;

    protected constructor() {
    }

    public get vendorList() : VendorList|undefined {
        return this._vendorList;
    }

    protected set vendorList(vendorList: VendorList|undefined) {
        this._vendorList = vendorList;
    }

    public get tcString() : TCString|undefined {
        return this._tcString;
    }

    protected set tcString(tcString: TCString|undefined) {
        this._tcString = tcString;
    }

    public get addtlConsent() : AddtlConsent|undefined {
        return this._addtlConsent;
    }

    protected set addtlConsent(addtlConsent: AddtlConsent|undefined) {
        this._addtlConsent = addtlConsent;
    }

    public get customConsent() : CustomConsent|undefined {
        return this._customConsent;
    }

    protected set customConsent(customConsent: CustomConsent|undefined) {
        this._customConsent = customConsent;
    }

    public get userSelection() : UserSelection|undefined {
        return this._userSelection;
    }

    protected set userSelection(userSelection: UserSelection|undefined) {
        this._userSelection = userSelection;
    }

    public get consentAcquired() : boolean {
        return this._consentAcquired;
    }

    protected set consentAcquired(consentAcquired: boolean) {
        this._consentAcquired = consentAcquired;
    }

    public abstract load(): Promise<void>;

    public async unproxy(): Promise<CMP> {
        return Promise.reject(`Unproxy not implemented`);
    }

    /**
     * Devuelve los propósitos del Cmp.
     * Incluyen información de la GVL así como de las preferencias
     * del usuario.
     */
    public purposes() : Purpose[] {
        const purposes:Purpose[] = [];
        this.vendorList?.purposesIds().forEach((purposeId:number) => {
            const purpose: Purpose|null = Purpose.build(purposeId);
            if (purpose) {
                purposes.push(purpose);
            }
        });
        return purposes;
    }

    /**
     * Devuelve los propósitos especiales del Cmp.
     * Incluyen información de la GVL.
     */
    public specialPurposes() : SpecialPurpose[] {
        const purposes:SpecialPurpose[] = [];
        this.vendorList?.specialPurposesIds().forEach((purposeId:number) => {
            const purpose: SpecialPurpose|null = SpecialPurpose.build(purposeId);
            if (purpose) {
                purposes.push(purpose);
            }
        });
        return purposes;
    }

    /**
     * Devuelve las características del Cmp.
     * Incluyen información de la GVL.
     */
    public features() : Feature[] {
        const features:Feature[] = [];
        this.vendorList?.featuresIds().forEach((featureId:number) => {
            const feature: Feature|null = Feature.build(featureId);
            if (feature) {
                features.push(feature);
            }
        });
        return features;
    }

    /**
     * Devuelve las características especiales del Cmp.
     * Incluyen información de la GVL así como de las preferencias
     * del usuario.
     */
    public specialFeatures() : SpecialFeature[] {
        const specialFeatures:SpecialFeature[] = [];
        this.vendorList?.specialFeaturesIds().forEach((specialFeatureId:number) => {
            const specialFeature: SpecialFeature|null = SpecialFeature.build(specialFeatureId);
            if (specialFeature) {
                specialFeatures.push(specialFeature);
            }
        });
        return specialFeatures;
    }

    /**
     * Devuelve los propósitos de LI del Cmp.
     * Incluyen información de la GVL así como de las preferencias
     * del usuario.
     */
    public purposesLI() : LIPurpose[] {
        const purposes:LIPurpose[] = [];
        this.vendorList?.purposesLIIds().forEach((purposeId:number) => {
            const purpose: LIPurpose|null = LIPurpose.build(purposeId);
            if (purpose) {
                purposes.push(purpose);
            }
        });
        return purposes;
    }

    /**
     * Devuelve los vendors del Cmp.
     * Incluyen información de la GVL así
     * como de las preferencias del usuario.
     */
    public vendors() : Vendor[] {
        const vendors:Vendor[] = [];
        this.vendorList?.vendorIds().forEach((vendorId:number) => {
            const vendor: Vendor|null = Vendor.build(vendorId);
            if (vendor) {
                vendors.push(vendor);
            }
        });
        return vendors.sort((vendorA:Vendor, vendorB:Vendor) => {
            return vendorA.name.localeCompare(vendorB.name);
        });
    }

    /**
     * Devuelve los vendors del Cmp con interés legítimo.
     * Incluyen información de la GVL así
     * como de las preferencias del usuario.
     */
    public vendorsLI() : VendorLI[] {
        const vendors:VendorLI[] = [];
        this.vendorList?.vendorLIIds().forEach((vendorId:number) => {
            const vendor: VendorLI|null = VendorLI.build(vendorId);
            if (vendor) {
                vendors.push(vendor);
            }
        });
        return vendors.sort((vendorA:Vendor, vendorB:Vendor) => {
            return vendorA.name.localeCompare(vendorB.name);
        });
    }

    /**
     * Devuelve los vendors adicionales del Cmp.
     * Incluye información de la GVL así
     * como de las preferencias de usuario.
     */
    public additionalVendors() : AdditionalVendor[] {
        const vendors:AdditionalVendor[] = [];
        this.vendorList?.additionalVendorsIds().forEach((vendorId:number) => {
            const vendor: AdditionalVendor|null = AdditionalVendor.build(vendorId);
            if (vendor) {
                vendors.push(vendor);
            }
        });
        return vendors.sort((vendorA:AdditionalVendor, vendorB:AdditionalVendor) => {
            return vendorA.name.localeCompare(vendorB.name);
        });
    }

    /**
     * Devuelve los vendors personalizados del Cmp.
     * Incluyen información de la GVL así
     * como de las preferencias del usuario.
     */
    public customVendors() : CustomVendor[] {
        const vendors:CustomVendor[] = [];
        this.vendorList?.customVendorsIds().forEach((vendorId:number) => {
            const vendor: CustomVendor|null = CustomVendor.build(vendorId);
            if (vendor) {
                vendors.push(vendor);
            }
        });
        return vendors.sort((vendorA:CustomVendor, vendorB:CustomVendor) => {
            return vendorA.name.localeCompare(vendorB.name);
        });
    }

    /**
     * Acepta todos los consentimientos y guarda el estado.
     */
    public acceptAll(): void {
        log.debug('Accepting all vendors for CMP Proxy');

        // Actualizamos metadata
        Metadata().acceptanceLevel = 1;
        Metadata().save();

        // Notificamos al estado
        cmpStatus.userActionCompleted();
        cmpStatus.hideUI();

        // Persiste la información
        this.persistConsent(CMP_NORMAL_EXPIRED_DAYS);

        this.consentAcquired = true;

        // Lanzamos evento de confirmación de consentimiento
        EventManager.trigger(EventManager.EVENT_CMP_SUBMIT);
    }

    /**
     * Rechaza todos los consentimientos y guarda el estado.
     */
    public rejectAll(): void {
        log.debug('Rejecting all vendors for CMP Proxy');
        // Actualiza selección de usuario
        this.userSelection?.rejectAll();

        // Actualizamos metadata
        Metadata().acceptanceLevel = 0;
        Metadata().save();

        // Notificamos al estado
        cmpStatus.userActionCompleted();
        cmpStatus.hideUI();

        // Persiste la información
        this.persistConsent(CMP_CONT_EXPIRED_DAYS);

        this.consentAcquired = true;

        // Lanzamos evento de confirmación de consentimiento
        EventManager.trigger(EventManager.EVENT_CMP_SUBMIT);
    }

    /**
     * Guarda y acepta la configuración actual.
     */
    public abstract saveAndAcceptConfiguration() : void;

    /**
     * Indica si el usuario ha rechazado todos los consentimientos.
     */
    public isAllRejected() : boolean {
        let allRejected:boolean = true;
        allRejected = allRejected && this.vendorList?.purposesIds().filter((id:number) => {return this.tcString?.purposes[id] === true}).length == 0;
        allRejected = allRejected && this.vendorList?.purposesLIIds().filter((id:number) => {return this.tcString?.purposesLI[id] === true}).length == 0;
        allRejected = allRejected && this.vendorList?.specialFeaturesIds().filter((id:number) => {return this.tcString?.specialFeatures[id] === true}).length == 0;
        allRejected = allRejected && this.vendorList?.vendorIds().filter((id:number) => {return this.tcString?.vendors[id] === true}).length == 0;
        allRejected = allRejected && this.vendorList?.vendorLIIds().filter((id:number) => {return this.tcString?.vendorsLI[id] === true}).length == 0;
        allRejected = allRejected && this.vendorList?.additionalVendorsIds().filter((id:number) => {return this.addtlConsent?.vendors()[id] === true;}).length == 0;

        if (site.hashCustomConfig()) {
            allRejected = allRejected && this.vendorList?.customVendorsIds().filter((id:number) => {return this.customConsent?.vendors()[id] === true}).length == 0;
        }

        return allRejected;
    }

    /**
     * Indica si el usuario ha rechazado algún consentimiento.
     */
    public someoneRejected() : boolean {
        let someoneRejected:boolean = false;
        someoneRejected = someoneRejected || (this.vendorList?.purposesIds().filter(id => this.tcString?.purposes[id] !== true).length||0) > 0;
        someoneRejected = someoneRejected || (this.vendorList?.purposesLIIds().filter(id => this.tcString?.purposesLI[id] !== true).length||0) > 0;
        someoneRejected = someoneRejected || (this.vendorList?.specialFeaturesIds().filter(id => this.tcString?.specialFeatures[id] !== true).length||0) > 0;
        someoneRejected = someoneRejected || (this.vendorList?.vendorIds().filter(id => this.tcString?.vendors[id] !== true).length||0) > 0;
        someoneRejected = someoneRejected || (this.vendorList?.vendorLIIds().filter(id => this.tcString?.vendorsLI[id] !== true).length||0) > 0;
        someoneRejected = someoneRejected || (this.vendorList?.additionalVendorsIds().filter(id => this.addtlConsent?.vendors()[id] !== true).length||0) > 0;
        if (site.hashCustomConfig()) {
            someoneRejected = someoneRejected || (this.vendorList?.customVendorsIds().filter(id => this.customConsent?.vendors()[id] !== true).length||0) > 0;
        }

        return someoneRejected;
    }

    /**
     * Indicia si el usuario ha aceptado todos los consentimientos.
     */
    public isAllAccepted() : boolean {
        return !this.someoneRejected();
    }

    /**
     * Determina el nivel de aceptación de consentimientos.
     * 0 - Todos los consentimientos rechazados
     * 1 - Todos los consentimientos aceptados
     * 2 - Otro caso
     */
    public determineAcceptanceLevel() : number {
        return this.isAllRejected() ? 0 : (this.isAllAccepted() ? 1 : 2);
    }

    /**
     * Guarda la información de consentimiento.
     * @private
     */
    protected persistConsent(expireDays?: number) : void {
        if (expireDays === undefined) {
            this.tcString?.store(this.someoneRejected() ? CMP_CONT_EXPIRED_DAYS : CMP_NORMAL_EXPIRED_DAYS);
        } else {
            this.tcString?.store(expireDays);
        }
        this.addtlConsent?.save();
        this.customConsent?.save();
        Metadata().save();
    }

    /**
     * Versión del CMP.
     * Si existe una TCString se devuelve la versión con la que se creó.
     * Si no existe se devuelve la versión del CMP por defecto.
     */
    public version(): number {
        return this.tcString?.version || CMP_VERSION;
    }

    /**
     * Indica que GDPR ya ha sido aceptado por el usuario.
     * No se requiere acción por parte del usuario. Se notifica
     * al estado del CMP.
     */
    public gdprAlreadyAccepted() : void {
        cmpStatus.configureCMP();
    }

    /**
     * Indica el estado de acpetación de consentimientos.
     */
    public acceptanceLevel(): number {
        return Metadata().acceptanceLevel;
    }
}
